import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { useNotifications } from '@/hooks/useNotifications';
import { AppStateActions } from '@/reducers/appReducer';
import { Button } from '@/components/ui/button';
import { DialogBody, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { requestRestoreUserPassword } from '@editar-pdf/api';
import { useFormik } from 'formik';
import { Mail } from 'lucide-react';
import { useCallback, useContext, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LabeledField } from '@/components/LabeledField';

type LoginFormDataType = {
  email: string;
};

export function RestoreView() {
  const { t } = useTranslation('web', { keyPrefix: 'components.AuthModal' });
  const { dispatch } = useContext(Context);
  const { notifyError, notifySuccess } = useNotifications();
  const [loading, setLoading] = useState(false);
  const emailId = useId();

  const handleBack = () =>
    dispatch({
      type: AppStateActions.SET_AUTH_STATE,
      payload: { state: 'SIGN_IN' },
    });

  const handleRestore = useCallback(
    async (data: LoginFormDataType) => {
      setLoading(true);
      try {
        await requestRestoreUserPassword(data);
        dispatch({ type: AppStateActions.SET_AUTH_STATE, payload: {} });
        notifySuccess(t('successRestoreLinkSend'));
      } catch (e: unknown) {
        notifyError(getResponseError(e));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, notifyError, notifySuccess, t],
  );

  const loginForm = useFormik<LoginFormDataType>({
    initialValues: { email: '' },
    onSubmit: handleRestore,
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('restoreHeader')}</DialogTitle>
      </DialogHeader>

      <DialogBody>
        <form onSubmit={loginForm.handleSubmit}>
          <div className="flex flex-col gap-4">
            <LabeledField label={t('emailLabel')} htmlFor={emailId}>
              <Input
                id={emailId}
                required
                name="email"
                type="email"
                autoComplete="email"
                placeholder={t('emailPlaceholder')}
                value={loginForm.values.email}
                onChange={loginForm.handleChange}
              />
            </LabeledField>

            <Button type="submit" className="w-full" loading={loading}>
              {!loading && <Mail />}
              {t('restorePassword')}
            </Button>
          </div>
        </form>
      </DialogBody>

      <DialogFooter>
        <span className="text-ui text-muted-foreground">{t('havePassword')}</span>
        <Button variant="link" size="sm" className="px-0" onClick={handleBack}>
          {t('signIn')}
        </Button>
      </DialogFooter>
    </>
  );
}
